/**
 * The Reading stage (plan docs/plans/lms-plan.md 7.2, 6.1, 6.2 rule 9, 11.3,
 * D12; WP-40): the full document, every section's reading_md through MdLite
 * with the section titles as headings, each section opening with "Why this
 * matters" and closing with "What to remember", then the key takeaways (the
 * last reading section, 6.1), the glossary and the sources, and at the end
 * the "Mark as read" control that sets reading_done.
 *
 * PRINT (D12: the browser's print is the "download the document" path): a
 * print copy of the document is portalled to <body> with a print stylesheet
 * that hides everything else and forces dark text on white, so the printed
 * pages hold the whole document however the app shell clips its scrolling
 * <main>. The copy is display:none on screen. Nothing here uses raw HTML: the
 * stylesheet is a <style> element's text child.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Printer } from 'lucide-react';
import type { CourseContent, CourseSource } from '../../content/types';
import { MdLite } from '../../content/MdLite';
import { outlineBtnCls, primaryBtnCls } from '../../../ui/styles';
import { OpenSopButton } from '../../tabs/sops/OpenSopButton';
import { microLabelCls, noticeCls, readingCls } from './styles';

/** The attribute the print stylesheet keys on (a direct child of <body>). */
export const PRINT_ROOT_ATTR = 'data-lms-print-root';

/**
 * The print stylesheet. On screen the print copy is hidden; in print every
 * other child of <body> is hidden and the copy prints in full, dark on white
 * (the dark theme and the app shell never reach the paper).
 */
export const PRINT_CSS = [
  `@media screen { [${PRINT_ROOT_ATTR}] { display: none !important; } }`,
  '@media print {',
  '  html, body { height: auto !important; overflow: visible !important; background: white !important; }',
  `  body > *:not([${PRINT_ROOT_ATTR}]) { display: none !important; }`,
  `  [${PRINT_ROOT_ATTR}] { display: block !important; padding: 0 !important; }`,
  `  [${PRINT_ROOT_ATTR}] * { color: rgb(17 24 39) !important; background: transparent !important; box-shadow: none !important; }`,
  `  [${PRINT_ROOT_ATTR}] h2, [${PRINT_ROOT_ATTR}] h3 { break-after: avoid; page-break-after: avoid; }`,
  `  [${PRINT_ROOT_ATTR}] table, [${PRINT_ROOT_ATTR}] figure { break-inside: avoid; page-break-inside: avoid; }`,
  `  [${PRINT_ROOT_ATTR}] a[href]::after { content: " (" attr(href) ")"; font-size: 0.85em; }`,
  '  @page { margin: 18mm 16mm; }',
  '}',
].join('\n');

export interface ReadingStageProps {
  courseTitle: string;
  versionNo: number;
  content: CourseContent;
  sources: CourseSource[];
  readingDone: boolean;
  onMarkRead: () => Promise<boolean>;
  /** read_ack (11.3): the control says the document was opened and read in full. */
  acknowledgeFlow: boolean;
  /** A section to scroll to once (a review pointer from the quiz results). */
  target: string | null;
  onTargetShown: () => void;
  /** Where to go once the reading is marked read (the quiz or the acknowledgement). */
  nextLabel: string | null;
  nextDisabledReason: string | null;
  onNext: () => void;
  saveError: string | null;
  /**
   * 11.3, plan 18.27 (WP-46): an SOP course (category sop) shows "Open the SOP"
   * before "Mark as read"; the button opens the SOP file behind courseVersionId.
   */
  courseCategory?: string | null;
  courseId?: string | null;
  courseVersionId?: string | null;
  /**
   * docs/plans/sop-import-plan.md 4.4 item 3: on an SOP course the control that
   * says the document was opened and read waits for "Open the SOP" to have
   * opened the file. The player keeps the state across stages (fileState, and
   * onFileState when this stage learns it); without them the stage keeps its
   * own. 'unavailable' (the server has no file this person may open: no file,
   * or 404) releases the hold, so nobody is stuck; a passing failure does not.
   */
  fileState?: SopFileState;
  onFileState?: (state: SopFileState) => void;
}

/** Whether "Open the SOP" has opened the file in this visit (4.4 item 3). */
export type SopFileState = 'unknown' | 'opened' | 'unavailable';

/** The words under the held control (4.4 item 3). */
export const OPEN_FIRST_HINT = 'Open the SOP first: the button above opens it in a new tab.';

export function readingSectionId(sectionId: string): string {
  return `lms-reading-${sectionId}`;
}

function SourcesList({ sources }: { sources: CourseSource[] }) {
  return (
    <ol className="list-decimal space-y-1.5 pl-5 text-sm text-gray-700 dark:text-gray-300">
      {sources.map((s, i) => (
        <li key={i}>
          <span className="font-medium text-gray-900 dark:text-gray-100">{s.title}</span>
          {s.publisher ? `, ${s.publisher}` : ''}
          {s.url ? (
            <>
              {'. '}
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {s.url}
              </a>
            </>
          ) : null}
          {s.accessed_on ? ` (accessed ${s.accessed_on})` : ''}
          {s.note ? `. ${s.note}` : ''}
        </li>
      ))}
    </ol>
  );
}

/** The document itself: shared by the screen view and the print copy. */
function ReadingDocument({
  courseTitle,
  versionNo,
  content,
  sources,
  idPrefix,
  versionWord = 'Version',
}: {
  courseTitle: string;
  versionNo: number;
  content: CourseContent;
  sources: CourseSource[];
  /** Section anchors only on the screen copy (ids must be unique in the page). */
  idPrefix: boolean;
  /** "Training version" on an SOP course, so it is never read as the SOP's own label (4.3 D). */
  versionWord?: string;
}) {
  const sections = Array.isArray(content.sections) ? content.sections : [];
  const glossary = Array.isArray(content.glossary) ? content.glossary : [];
  const takeaways = Array.isArray(content.key_takeaways) ? content.key_takeaways : [];
  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <div className={microLabelCls}>{`${versionWord} ${versionNo}`}</div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{courseTitle}</h2>
      </header>
      {sections.map((section, i) => (
        <section key={section.id} id={idPrefix ? readingSectionId(section.id) : undefined} className="scroll-mt-4 space-y-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{`${i + 1}. ${section.title}`}</h3>
          {section.why_it_matters && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-gray-100">Why this matters: </span>
              {section.why_it_matters}
            </p>
          )}
          <MdLite source={section.reading_md} size="sm" />
          {Array.isArray(section.what_to_remember) && section.what_to_remember.length > 0 && (
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <div className={microLabelCls}>What to remember</div>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
                {section.what_to_remember.map((w, j) => (
                  <li key={j}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      ))}
      {takeaways.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Key takeaways</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
            {takeaways.map((t, j) => (
              <li key={j}>{t}</li>
            ))}
          </ul>
        </section>
      )}
      {glossary.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Glossary</h3>
          <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[minmax(0,10rem)_1fr]">
            {glossary.map((g) => (
              <div key={g.term} className="contents">
                <dt className="font-semibold text-gray-900 dark:text-gray-100">{g.term}</dt>
                <dd className="text-gray-700 dark:text-gray-300">{g.definition}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      {(content.not_covered || sources.length > 0) && (
        <section className="space-y-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Scope and sources</h3>
          {content.not_covered && <p className="text-sm text-gray-700 dark:text-gray-300">{content.not_covered}</p>}
          {sources.length > 0 && <SourcesList sources={sources} />}
        </section>
      )}
    </div>
  );
}

function PrintCopy({ children }: { children: ReactNode }) {
  if (typeof document === 'undefined' || !document.body) return null;
  return createPortal(
    <div data-lms-print-root="true" className="bg-white font-sans text-gray-900" data-testid="lms-print-root">
      <style>{PRINT_CSS}</style>
      {children}
    </div>,
    document.body
  );
}

export function ReadingStage(props: ReadingStageProps) {
  const {
    courseTitle,
    versionNo,
    content,
    sources,
    readingDone,
    onMarkRead,
    acknowledgeFlow,
    target,
    onTargetShown,
    nextLabel,
    nextDisabledReason,
    onNext,
    saveError,
    courseCategory = null,
    courseId = null,
    courseVersionId = null,
    fileState = 'unknown',
    onFileState,
  } = props;
  const [marking, setMarking] = useState(false);
  const [stateHere, setStateHere] = useState<SopFileState>('unknown');
  const isSop = courseCategory === 'sop' && Boolean(courseId);
  // 4.4 item 3: on an SOP course the file must have been opened before the control is offered,
  // unless the server has no file this person may open (then the hold would only trap them).
  const known: SopFileState = fileState === 'opened' || stateHere === 'opened'
    ? 'opened'
    : (fileState === 'unavailable' || stateHere === 'unavailable' ? 'unavailable' : 'unknown');
  const heldForOpen = isSop && !readingDone && known === 'unknown';
  const versionWord = courseCategory === 'sop' ? 'Training version' : 'Version';
  const noteFile = (state: SopFileState) => {
    setStateHere((prev) => (prev === 'opened' ? prev : state));
    if (onFileState) onFileState(state);
  };
  const rootRef = useRef<HTMLDivElement | null>(null);

  // A review pointer (7.2 "the sections to review") scrolls to its section once.
  useEffect(() => {
    if (!target) return;
    const el = rootRef.current?.querySelector(`#${readingSectionId(target)}`) as HTMLElement | null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
    if (el && typeof el.focus === 'function') {
      el.setAttribute('tabindex', '-1');
      el.focus();
    }
    onTargetShown();
  }, [target, onTargetShown]);

  const markRead = async () => {
    if (marking || readingDone || heldForOpen) return;
    setMarking(true);
    try {
      await onMarkRead();
    } finally {
      setMarking(false);
    }
  };

  const print = () => {
    if (typeof window.print === 'function') window.print();
  };

  return (
    <div className={readingCls} ref={rootRef} data-testid="lms-reading-stage">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {acknowledgeFlow
            ? 'Read the whole document, then confirm you have read it in full.'
            : 'The full document behind the slides. Read it through, then mark it as read.'}
        </p>
        <button type="button" className={outlineBtnCls()} onClick={print} data-testid="lms-print">
          <Printer className="h-4 w-4" aria-hidden="true" />
          Print or save as PDF
        </button>
      </div>

      {isSop && courseId && (
        <div className={`${noticeCls('primary')} mb-4 flex-wrap`} data-testid="lms-reading-sop">
          <span className="min-w-0 flex-1">Open the SOP and read it in full before you confirm below.</span>
          <OpenSopButton
            courseId={courseId}
            courseVersionId={courseVersionId}
            onOpened={() => noteFile('opened')}
            onUnavailable={() => noteFile('unavailable')}
          />
        </div>
      )}

      <article className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-8" data-testid="lms-reading">
        <ReadingDocument courseTitle={courseTitle} versionNo={versionNo} content={content} sources={sources} idPrefix versionWord={versionWord} />
      </article>

      <div className="mt-4 space-y-3" data-testid="lms-reading-end">
        {saveError && (
          <div className={noticeCls('amber')} role="status">
            {saveError}
          </div>
        )}
        <div aria-live="polite">
          {readingDone ? (
            <div className={noticeCls('green')} data-testid="lms-reading-done">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{acknowledgeFlow ? 'You confirmed you have read this document in full.' : 'Marked as read.'}</span>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!readingDone && (
            <button
              type="button"
              className={primaryBtnCls}
              onClick={markRead}
              disabled={marking || heldForOpen}
              aria-describedby={heldForOpen ? 'lms-mark-read-hint' : undefined}
              data-testid="lms-mark-read"
            >
              {acknowledgeFlow ? 'I have opened and read the document in full' : 'Mark as read'}
            </button>
          )}
          {heldForOpen && (
            <span id="lms-mark-read-hint" className="text-xs text-gray-500 dark:text-gray-400" data-testid="lms-mark-read-hint">
              {OPEN_FIRST_HINT}
            </span>
          )}
          {nextLabel && (
            <button
              type="button"
              className={readingDone ? primaryBtnCls : outlineBtnCls()}
              onClick={onNext}
              disabled={Boolean(nextDisabledReason)}
              title={nextDisabledReason ?? undefined}
              data-testid="lms-reading-next"
            >
              {nextLabel}
            </button>
          )}
          {nextDisabledReason && readingDone && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{nextDisabledReason}</span>
          )}
        </div>
      </div>

      <PrintCopy>
        <ReadingDocument courseTitle={courseTitle} versionNo={versionNo} content={content} sources={sources} idPrefix={false} versionWord={versionWord} />
      </PrintCopy>
    </div>
  );
}

export default ReadingStage;
